import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import {
    HasProduct,
    HasProductContext,
    ProductContext,
    ProductContextModel,
    withProduct,
} from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductDto, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import baseTheme from "@insite/mobius/globals/baseTheme";
import {
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    LoadingSpinner,
} from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AwesomeCart from "@thos/Assets/Icons/AwesomeCart";
import loadBundledProductsByCustomVariant from "@thos/Handlers/ProductDetails/BatchLoadProductsByCustomVariant";
import changeProductByVariantQty from "@thos/Handlers/ProductDetails/ChangeProductByVariantQty";
import ProductAddToCartButton from "@thos/Overrides/Components/ProductAddToCartButton";
import ProductContextAvailability from "@thos/Overrides/Components/ProductContextAvailability";
import CustomApplicationState from "@thos/Store/CustomApplicationState";
import cloneDeep from "lodash/cloneDeep";
import sortBy from "lodash/sortBy";
import * as React from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const mapStateToProps = (state: CustomApplicationState) => ({
    session: state.context.session,
    productsByVariant: state.ThosState.productsByVariantState.products,
    productInfos: state.ThosState.productsByVariantState.productInfosByProductId,
    attributeInfos: state.ThosState.productsByVariantState.attributeInfosByProductId,
    isLoading: state.ThosState.productsByVariantState.isLoading,
    attributeTypeLabel: state.ThosState.productsByVariantState.attributeTypeLabel,
    productSettings: getSettingsCollection(state).productSettings,
});

const mapDispatchToProps = {
    loadBundledProductsByCustomVariant,
    changeProductByVariantQty,
};

type Props = OwnProps &
    HasProductContext &
    WidgetProps &
    HasProduct &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory;

export interface ProductDetailsVariantTypesStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    addToCartButton?: ButtonPresentationProps;
    partNumberColumn: DataTableCellProps;
    descriptionCell?: DataTableCellProps;
    attributeColumn: DataTableCellProps;
    availabilityColumn: DataTableCellProps;
    qtyOrderedColumn: DataTableCellProps;
    priceColumn: DataTableCellProps;
    addToCartColumn?: DataTableCellProps;
    productDetailLink?: LinkPresentationProps;
    tableFixHead: InjectableCss;
    qtyOrderedField?: InjectableCss;
}

export const dataTableStyles: ProductDetailsVariantTypesStyles = {
    container: {
        css: css`
            overflow: auto;
            margin: 15px;
            border: 2px solid ${getColor("common.border")};
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    addToCartButton: {
        css: css`
            width: 100%;
            margin: 0 -1px -1px 0;
        `,
        icon: {
            src: AwesomeCart,
            position: "left",
        },
    },
    productDetailLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
    partNumberColumn: {
        css: css`
            padding: 3px !important;
            width: 5% !important;
        `,
    },
    descriptionCell: {
        css: css`
            width: 42% !important;
            white-space: pre-wrap;
            overflow: hidden;
            padding: 3px !important;
        `,
    },
    attributeColumn: {
        css: css`
            width: 15% !important;
            white-space: pre-wrap;
            overflow: hidden;
            padding: 10px !important;
        `,
    },
    availabilityColumn: {
        css: css`
            padding: 3px !important;
            width: 15% !important;
            white-space: pre-wrap;
            overflow: hidden;
        `,
    },
    qtyOrderedColumn: {
        css: css`
            padding: 3px !important;
            width: 8% !important;
        `,
    },
    priceColumn: {
        css: css`
            padding: 3px !important;
            width: 10% !important;
            white-space: pre-wrap;
            overflow: hidden;
        `,
    },
    addToCartColumn: {
        css: css`
            padding: 3px !important;
            width: 10% !important;
        `,
    },
    qtyOrderedField: {
        css: css`
            max-width: 80px !important;
        `,
    },
    tableFixHead: {
        css: css`
            height: 311px;
            overflow-y: auto;
            overflow-x: hidden;
            &::-webkit-scrollbar {
                width: 4px !important;
                overflow-y: auto;
            }
            &::-webkit-scrollbar-track {
                background: ${getColor("common.background")};
                overflow-y: auto;
            }
            &::-webkit-scrollbar-thumb {
                background-color: grey;
                border-radius: 7px !important;
                overflow-y: auto;
            }
            table {
                width: 100%;
                table-layout: fixed;
            }
            thead th {
                position: sticky;
                top: 0;
                z-index: 2;
            }

            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(theme, [
                    css`
                        width: 1570px;
                    `,
                    css`
                        width: 1570px;
                    `,
                    css`
                        width: 1570px;
                    `,
                    null,
                    null,
                ]);
            }}
        `,
    },
};

const styles = dataTableStyles;
const mapProductDtoToProductModel = (productLine: ProductDto) => {
    const product = cloneDeep(productLine) as any as ProductModel;
    product.id = productLine.id;
    product.productNumber = productLine.erpNumber;
    product.productTitle = productLine.shortDescription;
    product.customerProductNumber = productLine.customerName;

    product.unitOfMeasures =
        productLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;
    return product;
};

const ProductDetailsVariantTypes: FC<Props> = ({
    session,
    product,
    productsByVariant,
    productInfos,
    attributeInfos,
    attributeTypeLabel,
    loadBundledProductsByCustomVariant,
    changeProductByVariantQty,
    isLoading,
    history,
}) => {
    if (!product || !product.properties.variantTypeCustomDisplay || !product.properties.variantAttributeCustomDisplay) {
        return null;
    }
    const [firstLoad, setFirstLoad] = React.useState(false);
    const { isAuthenticated } = session;
    React.useEffect(() => {
        if (firstLoad) {
            return;
        }
        loadBundledProductsByCustomVariant({
            variantType: product.properties.variantTypeCustomDisplay,
            parentProductId: product.id,
            attributeType: product.properties.variantAttributeCustomDisplay,
        });
        setFirstLoad(true);
    }, []);

    if (!productsByVariant || productsByVariant.length === 0) {
        return null;
    }
    const productList = productsByVariant ? sortBy(productsByVariant, o => o.erpNumber) : [];
    return (
        <>
            {isLoading && (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner}></LoadingSpinner>
                </StyledWrapper>
            )}
            {!isLoading && (
                <StyledWrapper {...styles.container}>
                    <StyledWrapper {...styles.tableFixHead}>
                        <DataTable>
                            <DataTableHead>
                                <DataTableHeader {...styles.partNumberColumn}>{translate("Part #")}</DataTableHeader>
                                <DataTableHeader {...styles.descriptionCell}>
                                    {translate("Description")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.attributeColumn}>{attributeTypeLabel}</DataTableHeader>
                                <DataTableHeader {...styles.availabilityColumn}>
                                    {translate("Availability at Selected Warehouse")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.qtyOrderedColumn}>
                                    {translate("QTY Ordered")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.priceColumn}>{translate("Price")}</DataTableHeader>
                                <DataTableHeader {...styles.addToCartColumn}>
                                    {translate("Add to Cart")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody data-test-selector="list-body">
                                {productList?.map(product => {
                                    const productModel = mapProductDtoToProductModel(product);
                                    const productDetailPath = product.productDetailUrl;
                                    const onClickRouteHandler = () => {
                                        history.push(productDetailPath);
                                    };
                                    const productContext: ProductContextModel = {
                                        product: productModel,
                                        productInfo: productInfos[product.id],
                                        onQtyOrderedChanged: qtyOrdered => {
                                            changeProductByVariantQty({
                                                productId: product.id,
                                                unitOfMeasure: productInfos[product.id].unitOfMeasure,
                                                qtyOrdered,
                                                product: productModel,
                                                productInfo: productInfos[product.id],
                                            });
                                        },
                                    };
                                    return (
                                        <ProductContext.Provider
                                            value={productContext}
                                            data-test-selector="widget_ProductsByVariantType"
                                            key={product.id}
                                        >
                                            <DataTableRow key={product.id}>
                                                <DataTableCell {...styles.partNumberColumn}>
                                                    <Link
                                                        {...styles.productDetailLink}
                                                        href={productDetailPath}
                                                        alt={`See product details for ${product.shortDescription}`}
                                                        onClick={() => onClickRouteHandler()}
                                                        data-test-selector="partNumberLink"
                                                    >
                                                        {product.erpNumber}
                                                    </Link>
                                                </DataTableCell>

                                                <DataTableCell {...styles.descriptionCell}>
                                                    {product.shortDescription}
                                                </DataTableCell>
                                                <DataTableCell {...styles.attributeColumn}>
                                                    {attributeInfos![product.id]?.value}
                                                </DataTableCell>
                                                <DataTableCell {...styles.availabilityColumn}>
                                                    {isAuthenticated ? (
                                                        <ProductContextAvailability
                                                            isProductDetailsPage={false}
                                                            hideLabel={true}
                                                            hideCDC={true}
                                                        />
                                                    ) : (
                                                        siteMessage("Inventory_SignInForAvailability")
                                                    )}
                                                </DataTableCell>

                                                <DataTableCell {...styles.qtyOrderedColumn}>
                                                    <ProductQuantityOrdered
                                                        labelOverride=""
                                                        extendedStyles={styles.qtyOrderedField}
                                                    />
                                                </DataTableCell>

                                                <DataTableCell {...styles.priceColumn}>
                                                    <ProductPrice
                                                        showLabel={false}
                                                        showSavings={false}
                                                        product={productContext}
                                                    />
                                                </DataTableCell>
                                                <DataTableCell {...styles.addToCartColumn}>
                                                    <ProductAddToCartButton
                                                        data-test-selector={`actionsAddToCart${product.id}`}
                                                        extendedStyles={styles.addToCartButton}
                                                    />
                                                </DataTableCell>
                                            </DataTableRow>
                                        </ProductContext.Provider>
                                    );
                                })}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withProduct(ProductDetailsVariantTypes))),
    definition: {
        displayName: "Product Details Variant Types",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
